<script lang="ts">
  import { Block } from '@reuters-graphics/graphics-components';
  import { onMount } from 'svelte';
  import { assets } from '$app/paths';
  import {
    drawChangeLoopLimaconBottom,
    drawChangeLoopLimaconTop,
    drawParagraphLimacon,
  } from './limaconFunctions.js';
  import {
    figureData,
    STROKE_COLOR_PRIMARY,
    STROKE_COLOR_SECONDARY,
    STROKE_WIDTH,
    FIGURE_SCALE,
    GRID_COLOR,
    GRID_STROKE_WIDTH,
  } from './figureData.js';

  interface Props {
    subhead?: string;
    figures?: Array<{ title?: string; subtitle?: string }>;
    legendText?: {
      skateChange?: string;
      reverseDirection?: string;
    };
  }

  let { subhead, figures: figureOverrides = [], legendText }: Props = $props();

  // Merge CMS content with hardcoded figureData
  const figures = figureData.map((fig, i) => ({
    ...fig,
    title: figureOverrides[i]?.title || fig.title,
    subtitle: figureOverrides[i]?.subtitle || fig.subtitle,
  }));

  let changeLoopLimaconBottomPathData = $state('');
  let changeLoopLimaconTopPathData = $state('');
  let changeLoopLimaconBottomPathLength = $state(0);
  let changeLoopLimaconTopPathLength = $state(0);
  let paragraphLimaconPathData = $state('');
  let paragraphLimaconPathLength = $state(0);
  let visibleFigures = $state([]);

  onMount(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(
              (entry.target as HTMLElement).dataset.index || '0'
            );
            if (!visibleFigures.includes(index)) {
              visibleFigures = [...visibleFigures, index];

              // Calculate cumulative delays for staggered animation
              // Each figure starts when the previous figure is 1/10th complete
              let baseDelay = 0;
              if (index === 1) baseDelay = 500; // After 1/10 of Circle Eight (5s * 0.1)
              if (index === 2) baseDelay = 1250; // After 1/10 of Serpentine (7.5s * 0.1 = 750ms) + 500ms
              if (index === 3) baseDelay = 1750; // After 1/10 of Three (5s * 0.1 = 500ms) + 1250ms
              if (index === 4) baseDelay = 2250; // After 1/10 of Bracket (5s * 0.1 = 500ms) + 1750ms
              if (index === 5) baseDelay = 3030; // After 1/10 of Double Three (7.8s * 0.1 = 780ms) + 2250ms
              if (index === 6) baseDelay = 3780; // After 1/10 of Rocker (7.5s * 0.1 = 750ms) + 3030ms
              if (index === 7) baseDelay = 4530; // After 1/10 of Paragraph Loop (7.5s * 0.1 = 750ms) + 3780ms

              // Draw limacon paths for Change Loop figure (now at index 7)
              if (index === 7) {
                const changeLoopBottom = drawChangeLoopLimaconBottom();
                changeLoopLimaconBottomPathData = changeLoopBottom.pathData;
                changeLoopLimaconBottomPathLength = changeLoopBottom.pathLength;

                const changeLoopTop = drawChangeLoopLimaconTop();
                changeLoopLimaconTopPathData = changeLoopTop.pathData;
                changeLoopLimaconTopPathLength = changeLoopTop.pathLength;
              } else if (index === 6) {
                const paragraph1 = drawParagraphLimacon();
                paragraphLimaconPathData = paragraph1.pathData;
                paragraphLimaconPathLength = paragraph1.pathLength;
              }

              // Trigger animations for Change Loop or Paragraph Loop
              if (index === 7 || index === 6) {
                // Wait for DOM update before triggering animations
                setTimeout(() => {
                  const svg = entry.target.querySelector('svg');
                  if (svg) {
                    const animateElements = svg.querySelectorAll('animate');
                    animateElements.forEach((anim, animIndex) => {
                      let delay = baseDelay;
                      if (index === 6) {
                        // CHANGE LOOP: specific timing pattern
                        if (animIndex === 0) delay += 0;
                        else if (animIndex === 1) delay += 3750;
                        else if (animIndex === 2) delay += 1250;
                        else if (animIndex === 3) delay += 5000;
                      } else if (index === 7) {
                        // PARAGRAPH LOOP: stagger animations with 2.5s delays
                        // animIndex 0: top red at 0s
                        // animIndex 1: bottom red at 2.5s
                        // animIndex 2: top blue at 5s
                        // animIndex 3: bottom blue at 7.5s
                        if (animIndex === 0) delay += 0;
                        else if (animIndex === 1) delay += 2500;
                        else if (animIndex === 2) delay += 5000;
                        else if (animIndex === 3) delay += 7500;
                      }

                      setTimeout(() => {
                        anim.beginElement();
                      }, delay);
                    });
                  }
                }, 50);
              } else {
                const svg = entry.target.querySelector('svg');
                if (svg) {
                  const animateElements = svg.querySelectorAll('animate');
                  animateElements.forEach((anim, animIndex) => {
                    let delay = baseDelay;
                    if (index === 0) {
                      delay += animIndex === 0 ? 0 : 2500;
                    } else if (index === 1) {
                      if (animIndex === 0) delay += 1250;
                      else if (animIndex === 1) delay += 0;
                      else if (animIndex === 2) delay += 3750;
                      else if (animIndex === 3) delay += 5000;
                    } else if (index === 2) {
                      // THREE figure: top paths at 0s, bottom paths at 2.5s
                      // animIndex 0-1: top paths (three-top-base, three-top-solid)
                      // animIndex 2-3: bottom paths (three-bottom-base, three-bottom-solid)
                      delay += animIndex < 2 ? 0 : 2500;
                    } else if (index === 3) {
                      // BRACKET figure: top paths at 0s, bottom paths at 2.5s
                      // animIndex 0-1: top paths (topbaselayer, topsolid)
                      // animIndex 2-3: bottom paths (baselayer, solid)
                      delay += animIndex < 2 ? 0 : 2500;
                    } else if (index === 5) {
                      // ROCKER: Sequential animation with decreasing delays
                      // animIndex 4-5: MIDDLE LEFT (base + solid) at 0s
                      // animIndex 0-1: TOP CIRCLE (base + solid) at 1.875s
                      // animIndex 6-7: MIDDLE RIGHT (base + solid) at 4.5s
                      // animIndex 2-3: BOTTOM CIRCLE (base + solid) at 6.125s
                      if (animIndex === 4 || animIndex === 5)
                        delay += 0; // MIDDLE LEFT
                      else if (animIndex === 0 || animIndex === 1)
                        delay += 1875; // TOP CIRCLE
                      else if (animIndex === 6 || animIndex === 7)
                        delay += 4500; // MIDDLE RIGHT
                      else if (animIndex === 2 || animIndex === 3)
                        delay += 6125; // BOTTOM CIRCLE
                    } else if (index === 6) {
                      // CHANGE LOOP: specific timing pattern
                      if (animIndex === 0) delay += 0;
                      else if (animIndex === 1) delay += 3750;
                      else if (animIndex === 2) delay += 1250;
                      else if (animIndex === 3) delay += 5000;
                    } else if (index === 7) {
                      // PARAGRAPH LOOP: stagger animations with 2.5s delays
                      // animIndex 0: top red at 0s
                      // animIndex 1: bottom red at 2.5s
                      // animIndex 2: top blue at 5s
                      // animIndex 3: bottom blue at 7.5s
                      if (animIndex === 0)
                        delay += 0; // top red
                      else if (animIndex === 1)
                        delay += 2500; // bottom red
                      else if (animIndex === 2)
                        delay += 5000; // top blue
                      else if (animIndex === 3) delay += 7500; // bottom blue
                    } else if (index === 4) {
                      // DOUBLE THREE: Animate both circles with overlapping pattern
                      // Triple layer structure: base (animates), dashed (static), solid (animates)
                      // DOM order: 0-1=Path1, 2-3=Path2, 4-5=Path3, 6-7=Path4, 8-9=Path5, 10-11=Path6
                      // TOP CIRCLE (clockwise): Path 5 → Path 6+4 overlap
                      // BOTTOM CIRCLE (counter-clockwise): Path 1 → Path 3 → Path 2 (sequential)

                      // Top circle paths: Path 5 → Path 6+4 overlap (clockwise)
                      if (animIndex === 8 || animIndex === 9)
                        delay += 0; // Path 5 (base + solid) starts at 0ms (ends at 2000ms)
                      else if (animIndex === 10 || animIndex === 11)
                        delay += 2000; // Path 6 (base + solid) starts at 2000ms (ends at 4800ms)
                      else if (animIndex === 6 || animIndex === 7)
                        delay += 2000; // Path 4 (base + solid) starts at 2000ms (overlaps with Path 6)
                      // Bottom circle paths: Path 1 → Path 3 → Path 2 (sequential)
                      else if (animIndex === 0 || animIndex === 1)
                        delay += 4000; // Path 1 (base + solid) starts at 0ms (ends at 4000ms)
                      else if (animIndex === 4 || animIndex === 5)
                        delay += 3200; // Path 3 (base + solid) starts at 3200ms (ends at 4800ms)
                      else if (animIndex === 2 || animIndex === 3)
                        delay += 6000; // Path 2 (base + solid) starts at 6000ms (ends at 6800ms)
                    }

                    setTimeout(() => {
                      anim.beginElement();
                    }, delay);
                  });
                }
              }
            }
          }
        });
      },
      { threshold: 0.5 }
    );

    const cards = document.querySelectorAll('.figure-card');
    cards.forEach((card) => observer.observe(card));

    return () => observer.disconnect();
  });
</script>

{#if subhead}
  <div class="container">
    <Block>
      <div class="subhead figure-subtitle">{subhead}</div>
    </Block>
  </div>
{/if}

<div class="container">
  <Block>
    <div class="legend">
      <div class="legend-item">
        <svg width="20" height="6" xmlns="http://www.w3.org/2000/svg">
          <line
            x1="0"
            y1="3"
            x2="10"
            y2="3"
            stroke={STROKE_COLOR_PRIMARY}
            stroke-width="4"
          />
          <line
            x1="10"
            y1="3"
            x2="20"
            y2="3"
            stroke={STROKE_COLOR_SECONDARY}
            stroke-width="4"
          />
        </svg>
        <span class="legend-text"
          >{legendText?.skateChange || 'Skate change'}</span
        >
      </div>
      <div class="legend-item">
        <svg width="20" height="6" xmlns="http://www.w3.org/2000/svg">
          <line
            x1="0"
            y1="3"
            x2="20"
            y2="3"
            stroke={STROKE_COLOR_SECONDARY}
            stroke-width="4"
            stroke-dasharray="3,3"
          />
        </svg>
        <span class="legend-text"
          >{legendText?.reverseDirection || 'Reverse direction'}</span
        >
      </div>
    </div>
  </Block>
</div>

<div class="container">
  <div class="figures-grid">
    {#each figures as figure, index}
      <div class="figure-card" data-index={index}>
        <div class="figure-title">{figure.title}</div>
        <div class="figure-subtitle">{figure.subtitle}</div>
        <svg
          width={140 * FIGURE_SCALE}
          height={(figure.height / 2) * FIGURE_SCALE}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 280 {figure.height}"
        >
          {#if figure.useParagraphLoop}
            <g>
              <!-- Grid lines for reference -->
              <line
                x1="140"
                y1="0"
                x2="140"
                y2="500"
                stroke={GRID_COLOR}
                stroke-width={GRID_STROKE_WIDTH}
                stroke-dasharray="5,5"
              />
              <line
                x1="0"
                y1="250"
                x2="280"
                y2="250"
                stroke={GRID_COLOR}
                stroke-width={GRID_STROKE_WIDTH}
                stroke-dasharray="5,5"
              />

              <!-- Top limaçon rotated 90 degrees - RED path animates first -->
              <g transform="rotate(90 140 250)">
                <path
                  d={paragraphLimaconPathData}
                  fill="none"
                  stroke={STROKE_COLOR_PRIMARY}
                  stroke-width={STROKE_WIDTH}
                  stroke-dasharray={paragraphLimaconPathLength}
                  stroke-dashoffset={paragraphLimaconPathLength}
                >
                  <animate
                    id="paragraphTopRed"
                    attributeName="stroke-dashoffset"
                    from={paragraphLimaconPathLength}
                    to="0"
                    begin="indefinite"
                    dur="2.5s"
                    fill="freeze"
                  />
                </path>
              </g>

              <!-- Bottom limaçon rotated -90 degrees - RED path animates second -->
              <g transform="rotate(-90 140 250)">
                <path
                  d={paragraphLimaconPathData}
                  fill="none"
                  stroke={STROKE_COLOR_PRIMARY}
                  stroke-width={STROKE_WIDTH}
                  stroke-dasharray={paragraphLimaconPathLength}
                  stroke-dashoffset={paragraphLimaconPathLength}
                >
                  <animate
                    id="paragraphBottomRed"
                    attributeName="stroke-dashoffset"
                    from={paragraphLimaconPathLength}
                    to={paragraphLimaconPathLength * 2}
                    begin="indefinite"
                    dur="2.5s"
                    fill="freeze"
                  />
                </path>
              </g>

              <!-- Top limaçon BLUE path - animates third -->
              <g transform="rotate(90 140 250)">
                <path
                  d={paragraphLimaconPathData}
                  fill="none"
                  stroke={STROKE_COLOR_SECONDARY}
                  stroke-width={STROKE_WIDTH}
                  stroke-dasharray={paragraphLimaconPathLength}
                  stroke-dashoffset={paragraphLimaconPathLength}
                >
                  <animate
                    id="paragraphTopBlue"
                    attributeName="stroke-dashoffset"
                    from={paragraphLimaconPathLength}
                    to="0"
                    begin="indefinite"
                    dur="2.5s"
                    fill="freeze"
                  />
                </path>
              </g>

              <!-- Bottom limaçon BLUE path - animates fourth -->
              <g transform="rotate(-90 140 250)">
                <path
                  d={paragraphLimaconPathData}
                  fill="none"
                  stroke={STROKE_COLOR_SECONDARY}
                  stroke-width={STROKE_WIDTH}
                  stroke-dasharray={paragraphLimaconPathLength}
                  stroke-dashoffset={paragraphLimaconPathLength}
                >
                  <animate
                    id="paragraphBottomBlue"
                    attributeName="stroke-dashoffset"
                    from={paragraphLimaconPathLength}
                    to={paragraphLimaconPathLength * 2}
                    begin="indefinite"
                    dur="2.5s"
                    fill="freeze"
                  />
                </path>
              </g>
            </g>
          {:else if figure.useChangeLoop}
            <g transform="rotate(180 140 250)">
              <!-- Grid lines for reference -->
              <line
                x1="140"
                y1="0"
                x2="140"
                y2="500"
                stroke={GRID_COLOR}
                stroke-width={GRID_STROKE_WIDTH}
                stroke-dasharray="5,5"
              />
              <line
                x1="0"
                y1="250"
                x2="280"
                y2="250"
                stroke={GRID_COLOR}
                stroke-width={GRID_STROKE_WIDTH}
                stroke-dasharray="5,5"
              />

              <!-- Serpentine pattern -->
              <!-- Middle circle - top half (from 140,160 to 140,300) -->
              <path
                d="M 140,160 A 70,70 0 0,1 140,300"
                fill="none"
                stroke={STROKE_COLOR_PRIMARY}
                stroke-width={STROKE_WIDTH}
                stroke-dasharray="220"
                stroke-dashoffset="220"
              >
                <animate
                  attributeName="stroke-dashoffset"
                  from="220"
                  to="0"
                  begin="indefinite"
                  dur="1.25s"
                  fill="freeze"
                />
              </path>

              <!-- Middle circle - bottom half (from 140,300 to 140,160) -->
              <path
                d="M 140,300 A 70,70 0 0,1 140,160"
                fill="none"
                stroke={STROKE_COLOR_SECONDARY}
                stroke-width={STROKE_WIDTH}
                stroke-dasharray="220"
                stroke-dashoffset="220"
              >
                <animate
                  attributeName="stroke-dashoffset"
                  from="220"
                  to="0"
                  begin="indefinite"
                  dur="1.25s"
                  fill="freeze"
                />
              </path>

              <g transform="rotate(-90 140 250)">
                <!-- The bottom limaçon path with animation -->
                <path
                  d={changeLoopLimaconBottomPathData}
                  fill="none"
                  stroke={STROKE_COLOR_PRIMARY}
                  stroke-width={STROKE_WIDTH}
                  stroke-dasharray={changeLoopLimaconBottomPathLength}
                  stroke-dashoffset={changeLoopLimaconBottomPathLength}
                >
                  <animate
                    attributeName="stroke-dashoffset"
                    from={changeLoopLimaconBottomPathLength}
                    to={changeLoopLimaconBottomPathLength * 2}
                    begin="indefinite"
                    dur="2.5s"
                    fill="freeze"
                  />
                </path>

                <!-- Top limaçon at (140, 160) rotated 180 degrees with animation -->
                <g transform="rotate(180 248 205)">
                  <path
                    d={changeLoopLimaconTopPathData}
                    fill="none"
                    stroke={STROKE_COLOR_SECONDARY}
                    stroke-width={STROKE_WIDTH}
                    stroke-dasharray={changeLoopLimaconTopPathLength}
                    stroke-dashoffset={changeLoopLimaconTopPathLength}
                  >
                    <animate
                      attributeName="stroke-dashoffset"
                      from={changeLoopLimaconTopPathLength}
                      to="0"
                      begin="indefinite"
                      dur="2.5s"
                      fill="freeze"
                    />
                  </path>
                </g>
              </g>
            </g>
          {:else}
            {@html figure.svg}
          {/if}
        </svg>
      </div>
    {/each}
  </div>
</div>

<style>
  .container {
    width: 100%;
    margin: 0;
    padding: 20px;
  }

  .figures-grid {
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    gap: 30px;
    margin-top: 30px;
  }

  /* Center first two items (Circle Eight and Serpentine) on same row */
  .figure-card:nth-child(1) {
    grid-column: 2 / 4;
    grid-row: 1;
  }

  .figure-card:nth-child(2) {
    grid-column: 4 / 6;
    grid-row: 1;
  }

  /* Remaining items in 3-column layout starting row 2 */
  .figure-card:nth-child(3) {
    grid-column: 1 / 3;
    grid-row: 2;
  }

  .figure-card:nth-child(4) {
    grid-column: 3 / 5;
    grid-row: 2;
  }

  .figure-card:nth-child(5) {
    grid-column: 5 / 7;
    grid-row: 2;
  }

  .figure-card:nth-child(6) {
    grid-column: 1 / 3;
    grid-row: 3;
  }

  .figure-card:nth-child(7) {
    grid-column: 3 / 5;
    grid-row: 3;
  }

  .figure-card:nth-child(8) {
    grid-column: 5 / 7;
    grid-row: 3;
  }

  @media (max-width: 768px) {
    .figures-grid {
      gap: 0;
    }

    .figure-card {
      padding: 5px;
    }

    .figure-card svg {
      width: 100%;
      max-width: 140px;
      height: auto;
    }

    .figure-title {
      font-size: 1em;
    }

    .figure-subtitle {
      font-size: 0.85em;
      min-height: 3em;
    }
  }

  @media (max-width: 768px) {
    .figures-grid {
      gap: 0;
    }

    .container {
      padding: 10px;
    }

    .figure-card {
      padding: 5px;
    }

    .figure-card svg {
      width: 100%;
      max-width: 100px;
      height: auto;
    }

    .figure-title {
      font-size: 0.875em;
    }

    .figure-subtitle {
      display: none;
    }
  }

  .figure-card {
    background: #eff9fb;
    padding: 5px;
    border-radius: 8px;
    /* box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1); */
  }

  .figure-title {
    text-align: center;
    font-weight: bold;
    color: #2c3e50;
    font-size: 1.2em;
    min-height: 1.5em;
  }

  .figure-subtitle {
    text-align: center;
    margin-bottom: 15px;
    font-size: 1em;
    color: #666;
    min-height: 3em;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .subhead {
    text-align: left;
    justify-content: flex-start;
    font-size: 1em;
  }

  .legend {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 30px;
    margin: 30px 0;
    font-size: 1em;
  }

  .legend-item {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .legend-text {
    color: #666;
  }

  svg {
    display: block;
    margin: 0 auto;
    background-color: #eff9fb;
  }

  :global(body) {
    margin: 0;
    background-color: #f5f5f5;
    font-family: Arial, sans-serif;
  }
</style>
